// Utilidad compartida para subir fotos de productos al endpoint upload_photo.
// Comprime la imagen a 800px de ancho preservando proporciones y la sube al backend.

import { webserver } from "@/services/webserver.js";

/**
 * Comprime un File de imagen a JPEG con ancho maximo de `maxWidth` px.
 * @param {File} file
 * @param {number} maxWidth ancho objetivo (default 800)
 * @returns {Promise<string>} data URL (image/jpeg) de la imagen comprimida
 */
export function compressImage(file, maxWidth = 800) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error("No se proporciono archivo"));
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const ratio = img.width > maxWidth ? maxWidth / img.width : 1;
        const canvas = document.createElement("canvas");
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg"));
      };
      img.onerror = () => reject(new Error("Error al cargar la imagen"));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error("Error al leer el archivo"));
    reader.readAsDataURL(file);
  });
}

/**
 * Sube una foto al endpoint upload_photo.
 * @param {Object} params
 * @param {string} params.store  store_id
 * @param {string} params.marca  carpeta destino (codigo de marca)
 * @param {string} params.picture nombre de archivo destino (ej. "ABC123.jpg")
 * @param {string} params.photoDataUrl data URL del archivo (JPEG/PNG)
 * @returns {Promise<Object>} respuesta del backend
 */
export function uploadPhoto({ store, marca, picture, photoDataUrl }) {
  return new Promise((resolve, reject) => {
    const qry = {
      store,
      photo: photoDataUrl,
      marca,
      picture,
    };
    webserver(
      "upload_photo",
      qry,
      (response) => resolve(response),
      (err) => reject(err)
    );
  });
}

/**
 * Pipeline completo: comprime y sube. El nombre destino se deriva de `refe`.
 * @param {Object} params
 * @param {File} params.file
 * @param {Object} params.item objeto del producto (usa marca y refe)
 * @param {string} params.store store_id
 * @param {number} [params.maxWidth=800]
 * @returns {Promise<{photoDataUrl: string, picture: string, response: any}>}
 */
export async function compressAndUploadProductPhoto({ file, item, store, maxWidth = 800 }) {
  if (!file || !item || !store) {
    throw new Error("file, item y store son requeridos");
  }
  if (!item.marca || !item.refe) {
    throw new Error("El producto debe tener marca y refe para subir la foto");
  }
  const photoDataUrl = await compressImage(file, maxWidth);
  const picture = item.refe.toLowerCase() + ".jpg";
  const response = await uploadPhoto({
    store,
    marca: item.marca,
    picture,
    photoDataUrl,
  });
  return { photoDataUrl, picture, response };
}
